import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';
import Loadable from 'react-loadable';

import {Icon} from '@stubhub/bamboo';
import {isRequiredIf} from '@stubhub/custom-proptypes';
import {getDeliveryIcon} from '@stubhub/delivery-utils';
import GuaranteeCard from '@stubhub/react-guarantee';
import PayLaterMessage from '@stubhub/react-paylater-message';
import SecurePayments from '@stubhub/react-secure-payment';

import {hasInvalidTicket} from '../../../../../../helpers/invalid-ticket-helper';
import FEATURE_FLAGS from '../../../../../../utils/featureFlags';
import FakeArrowIcon from '../../../../components/icons/collapse';
import ConnectedCheckoutButton from '../../../../components/react-button-checkout';
import ErrorHandlingPage from '../../../../components/react-error-handling-page/index';
import SectionRowSeat from '../../../../components/react-section-row-seat';
import SectionRowSeatGA from '../../../../components/react-section-row-seat-ga';
import TicketsQuantityPicker from '../../../../components/react-tickets-quantity-picker';
import FanProtect from '../../../react-fan-protect';
import MoreInfo from '../../../react-more-info';
import DisclosureItem from '../../components/disclosure-item';
import IconBackground from '../../components/icon-background';
import ListingDisclosures from '../../components/listing-disclosures';
import LocalDeliveryNote from '../../components/local-delivery-note';
import DeliveryInfo from '../../components/react-delivery-info/container';
import ViewBYOSeat from '../../components/react-view-byo-seat';

import './index.scss';

const ImportantNote = Loadable({
  loader: () => import(/* webpackChunkName: "Affirm" */ '../../../../components/react-important-note'),
  loading: () => /* istanbul ignore next */ null,
});

const DeliveryIcon = ({deliveryId, ...props}) => {
  const iconName = getDeliveryIcon(deliveryId);

  return <Icon name={iconName} {...props} />;
};

DeliveryIcon.propTypes = {
  deliveryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

class DesktopBYO extends Component {
  static contextTypes = {
    globalRegistry: PropTypes.object,
  };

  constructor(props, context) {
    super(props, context);

    const {byoQuantity} = this.props;
    this.state = {
      initialQty: byoQuantity,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {byoQuantity} = this.props;
    if (byoQuantity !== nextProps.byoQuantity) {
      this.setState({
        initialQty: nextProps.byoQuantity,
      });
    }
  }

  handleQuantitySelection = (qtySelected) => {
    const {updateQuery} = this.props;
    updateQuery({
      byoQuantity: qtySelected,
    });
  };

  renderBYO() {
    const {
      className = '',
      listingId,
      venueConfig = {},
      sectionStats,
      listing = {},
      goToCheckout = () => {
        // Do nothing
      },
      onCloseBYO,
      isGA = false,
      showTicketRecoInd = false,
      partner = '',
      paymentMethods,
      listingLoading,
      checkoutParamsString,
      filteredPaymentMethods,
      eventCountry,
      isPaypalPayLaterEnabled,
      payLaterAmount,
      isPaypalLoading,
      paypalError,
      showFanProtectWarranty,
      showRestrictions,
      showMap,
      showLocalDeliveryNote,
    } = this.props;

    const {
      ticketTraits = [],
      deliveryMethods = [],
      rows = '',
      products = [],
      sectionName,
      venueConfigSectionId,
      valuePercentage = null,
    } = listing;
    const {initialQty} = this.state;
    const {id: venueConfigId, venueId, map = {}} = venueConfig;
    const {viewFromSection = false} = map;

    const deliveryMethodId = deliveryMethods?.[0]?.id;

    const {globalRegistry} = this.context;
    const showTrustMessages = globalRegistry && globalRegistry.getFeatureValue(FEATURE_FLAGS.common.trustMessages);
    const showUKCMAFeatures = globalRegistry && globalRegistry.getFeatureValue(FEATURE_FLAGS.event.showUKcmaFeatures);
    const isTicketInvalid = hasInvalidTicket({ticketTraits, showUKCMAFeatures});

    return (
      <div className={`RoyalBuildYourOrder EventRoyal__DesktopLayout__BYO ${className}`}>
        <button className="EventRoyal__DesktopLayout__BYO__header" onClick={onCloseBYO} type="button">
          <FakeArrowIcon />
          <div className="EventRoyal__DesktopLayout__BYO__header__BackText">
            <FormattedMessage
              id="event-royal.react-build-your-order.back-to-tickets"
              defaultMessage="Back to tickets"
            />
          </div>
        </button>
        <div className="EventRoyal__DesktopLayout__BYO__body">
          <ViewBYOSeat
            viewFromSection={viewFromSection}
            isGA={isGA}
            listing={listing}
            venueConfigId={venueConfigId}
            sectionStats={sectionStats}
            venueId={venueId}
            sectionId={venueConfigSectionId}
            showMap={showMap}
          />
          <div className="RoyalBuildYourOrder__body_section">
            {!isGA ? (
              <SectionRowSeat
                section={sectionName}
                row={rows}
                products={products}
                quantity={initialQty}
                valuePercentage={valuePercentage}
                showTicketRecoInd={showTicketRecoInd}
                partner={partner}
                showSeatInfo={showUKCMAFeatures}
              />
            ) : (
              <SectionRowSeatGA />
            )}
          </div>
          <TicketsQuantityPicker toggleOnQtyPickClose={this.handleQuantitySelection} />
          {isPaypalPayLaterEnabled && !paypalError && (
            <PayLaterMessage
              isLoading={isPaypalLoading}
              className="RoyalBuildYourOrder__paylater-message"
              buyerCountry={eventCountry}
              amount={payLaterAmount}
              forceReRender={[payLaterAmount]}
              textAlign="center"
            />
          )}
          <div className="RoyalBuildYourOrder__body_disclosures">
            {listingId && (
              <DisclosureItem keyProp="deliveryDate" className="RoyalBuildYourOrder_body_disclosures_delivery">
                <IconBackground>
                  <DeliveryIcon
                    deliveryId={deliveryMethodId}
                    color="#2f343b"
                    className="RoyalBuildYourOrder__body_disclosures_icon"
                  />
                </IconBackground>
                <DeliveryInfo listingId={listingId} namespace="selectedListing" listingLoading={listingLoading} />
              </DisclosureItem>
            )}
            <ListingDisclosures />
          </div>
          {showLocalDeliveryNote && <LocalDeliveryNote />}
          {isTicketInvalid && <ImportantNote className="RoyalBuildYourOrder__section__important-note" />}
          <MoreInfo className="RoyalBuildYourOrder__section" />
          {showFanProtectWarranty && showRestrictions && <FanProtect className="RoyalBuildYourOrder__section" />}
          {showTrustMessages && (
            <>
              <GuaranteeCard className="RoyalBuildYourOrder__section" />
              {paymentMethods && (
                <SecurePayments
                  methods={filteredPaymentMethods}
                  className="RoyalBuildYourOrder__section RoyalBuildYourOrder__secure_payments"
                />
              )}
            </>
          )}
        </div>
        <div className="RoyalBuildYourOrder__footer">
          <ConnectedCheckoutButton
            className="RoyalBuildYourOrder__footer_button_checkout"
            onClick={goToCheckout}
            checkoutParamsString={checkoutParamsString}
          />
        </div>
      </div>
    );
  }

  /*
   * If we have error equals true, namely all 3 api calls fail, we render an error page
   * otherwise we render BYO
   */
  render() {
    const {onCloseBYO, error} = this.props;
    if (error) {
      return <ErrorHandlingPage onClose={onCloseBYO} />;
    }

    return this.renderBYO();
  }
}

export default DesktopBYO;

const dataHasLoaded = (props) => props.loading === false;

DesktopBYO.propTypes = {
  onCloseBYO: PropTypes.func.isRequired,
  error: isRequiredIf(PropTypes.bool, dataHasLoaded),
  listingId: PropTypes.number.isRequired,
  showTicketRecoInd: isRequiredIf(PropTypes.bool, dataHasLoaded),
  isGA: isRequiredIf(PropTypes.bool, dataHasLoaded),
  byoQuantity: PropTypes.number.isRequired,
  goToCheckout: PropTypes.func,

  /**
   * The Class Name used to override the Styling for the component.
   *
   */
  className: PropTypes.string,
  venueConfig: PropTypes.object,
  listing: PropTypes.object,
  sectionStats: PropTypes.arrayOf(PropTypes.object),
  updateQuery: PropTypes.func,

  partner: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * Array of payment methods available
   */
  paymentMethods: PropTypes.array,

  /**
   * If listings are loading
   */
  listingLoading: PropTypes.bool,
  checkoutParamsString: PropTypes.string,

  /**
   * Filtered list of payment methods
   */
  filteredPaymentMethods: PropTypes.array,

  /**
   * Event country
   */
  eventCountry: PropTypes.string,

  /**
   * If Paypal Pay Later is enabled
   */
  isPaypalPayLaterEnabled: PropTypes.bool,

  /**
   * Paypal Pay Later amount
   */
  payLaterAmount: PropTypes.number,

  /**
   * If Paypal Pay Later is loading
   */
  isPaypalLoading: PropTypes.bool,

  /**
   * If exists Paypal Pay Later error
   */
  paypalError: PropTypes.bool,

  /**
   * Show Fan Protect warranty
   */
  showFanProtectWarranty: PropTypes.bool,

  /**
   * Show Fan Protect restrictions
   */
  showRestrictions: PropTypes.bool,

  /** Show byo map? */
  showMap: PropTypes.bool,

  /** Whether to show local delivery note */
  showLocalDeliveryNote: PropTypes.bool,
};

DesktopBYO.defaultProps = {
  className: '',
  venueConfig: {},
  listing: {},
  sectionStats: [],
};
